import React from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import { Navbar2, Footer2 } from "../../components";
import SeriesNav from "../../components/labs/SeriesNav";
import Zoomable from "../../components/Zoomable/Zoomable";
import { getRouteMeta } from "../../seoData";
import grafanaHosts from "../../images/labs/loki-grafana-hosts.png";

const RSYSLOG_LISTENER = `module(load="imudp")
input(type="imudp" port="514")

#module(load="imtcp")
#input(type="imtcp" port="514")`;

const RSYSLOG = `template(name="PerHostLogs" type="string" string="/var/log/remote/%HOSTNAME%.log")

template(name="UtcFileFormat" type="string"
  string="%timereported:::date-rfc3339,date-utc% %HOSTNAME% %syslogtag%%msg:::sp-if-no-1st-sp%%msg:::drop-last-lf%\\n")

*.* action(type="omfile" dynaFile="PerHostLogs" template="UtcFileFormat")`;

const COMPOSE = `services:
  loki:
    image: grafana/loki:latest
    container_name: loki
    ports:
      - "3100:3100"
    environment:
      - RETENTION_DAYS=\${RETENTION_DAYS:-7}
    volumes:
      - ./loki/loki-config.yaml:/etc/loki/local-config.yaml
      - loki-data:/loki
    command: -config.file=/etc/loki/local-config.yaml -config.expand-env=true
    restart: unless-stopped

  promtail:
    image: grafana/promtail:latest
    container_name: promtail
    volumes:
      - ./promtail/promtail-config.yaml:/etc/promtail/config.yaml
      - /var/log/remote:/var/log/remote:ro
    command: -config.file=/etc/promtail/config.yaml
    depends_on:
      - loki
    restart: unless-stopped

  grafana:
    image: grafana/grafana:latest
    container_name: grafana
    ports:
      - "3000:3000"
    volumes:
      - ./grafana-data:/var/lib/grafana
    depends_on:
      - loki
    restart: unless-stopped

volumes:
  loki-data:`;

const PROMTAIL = `server:
  http_listen_port: 9080
  grpc_listen_port: 0

positions:
  filename: /tmp/positions.yaml

clients:
  - url: http://loki:3100/loki/api/v1/push

scrape_configs:
  - job_name: remote_syslogs
    static_configs:
      - targets:
          - localhost
        labels:
          job: syslog
          __path__: /var/log/remote/*.log
    pipeline_stages:
      - regex:
          source: filename
          expression: '.*/(?P<host>[^/]+)\\.log'
      - labels:
          host:`;

const LOKI = `auth_enabled: false

server:
  http_listen_port: 3100

common:
  path_prefix: /loki
  storage:
    filesystem:
      chunks_directory: /loki/chunks
      rules_directory: /loki/rules
  replication_factor: 1
  ring:
    kvstore:
      store: inmemory

schema_config:
  configs:
    - from: 2020-10-24
      store: tsdb
      object_store: filesystem
      schema: v13
      index:
        prefix: index_
        period: 24h

limits_config:
  retention_period: \${RETENTION_DAYS}d

compactor:
  working_directory: /loki/compactor
  retention_enabled: true
  delete_request_store: filesystem`;

const NXLOG = String.raw`define ROOT C:\Program Files\nxlog
Moduledir %ROOT%\modules
CacheDir %ROOT%\data
Pidfile %ROOT%\data\nxlog.pid
SpoolDir %ROOT%\data
LogFile %ROOT%\data\nxlog.log

<Extension syslog>
    Module xm_syslog
</Extension>

<Input eventlog>
    Module im_msvistalog
    <QueryXML>
        <QueryList>
            <Query Id="0">
                <Select Path="Security">*</Select>
                <Select Path="System">*</Select>
                <Select Path="Application">*</Select>
            </Query>
        </QueryList>
    </QueryXML>
</Input>

<Output syslog_out>
    Module om_udp
    Host <LOG_SERVER_IP>
    Port 514
    Exec $Message = "EventID=" + string($EventID) + " " + $Message;
    Exec to_syslog_ietf();
</Output>

<Route eventlog_to_syslog>
    Path eventlog => syslog_out
</Route>`;

function DigitalForensicsHomelabLogging() {
  const { title, description: desc, image, url } = getRouteMeta(
    "/digital-forensics-homelab-logging"
  );

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} data-react-helmet="true"></meta>

        <meta property="og:type" content="website" data-react-helmet="true"></meta>
        <meta property="og:url" content={url} data-react-helmet="true"></meta>
        <meta property="og:title" content={title} data-react-helmet="true"></meta>
        <meta property="og:description" content={desc} data-react-helmet="true"></meta>
        <meta property="og:image" content={image} data-react-helmet="true"></meta>

        <meta property="twitter:card" content="summary_large_image"></meta>
        <meta property="twitter:url" content={url} data-react-helmet="true"></meta>
        <meta property="twitter:title" content={title} data-react-helmet="true"></meta>
        <meta property="twitter:description" content={desc} data-react-helmet="true"></meta>
        <meta property="twitter:image" content={image} data-react-helmet="true"></meta>
      </Helmet>
      <Navbar2 />
      <section id="blogs">
        <div className="blogs main">
          <div className="blogs-header pb-4">
            <Link to="/projects/" className="blog-back-link">
              <i className="fa-solid fa-arrow-left"></i>&nbsp;Back to Projects
            </Link>
            <h2 className="section-title">Digital Forensics HomeLab</h2>
            <div className="blog-meta">
              <span>
                <i className="fa-solid fa-clipboard-list"></i>Part 6:
                Centralized Logging with Loki
              </span>
            </div>
          </div>

          <SeriesNav current="/digital-forensics-homelab-logging/" />

          <p className="m-5">
            The first five parts built the lab and the things that can go
            wrong in it. This part adds the thing that lets me see what
            happened: a single server that every machine in the lab sends its
            logs to.
          </p>

          <h3>Why Logging Matters</h3>
          <p className="m-5">
            In an investigation, logs are usually the only record of what
            actually happened. A logon, a new account, a service starting, a
            firewall rule matching: if it wasn't written down somewhere, you
            can't reconstruct it later. The problem is where it gets written
            down. By default every machine keeps its own logs, which means an
            attacker who gets onto a host can clear them, and an analyst has
            to visit each machine one at a time to piece a timeline together.
          </p>
          <p className="m-5">
            Sending logs to one central place fixes both. The copy on the
            collector survives even if the original is wiped, and every source
            can be searched side by side. For a lab built around DFIR, that
            felt like something I needed before doing anything else with it.
          </p>

          <h3>Why Syslog</h3>
          <p className="m-5">
            <span className="fw-bolder">Syslog</span> is the closest thing the
            industry has to a common language for logs. Firewalls, routers,
            Linux hosts, and plenty of other equipment can all forward events
            as syslog messages to a remote server, usually over UDP or TCP
            port 514. Picking it meant I didn't have to build a separate
            integration for every source.
          </p>
          <p className="m-5">
            Windows is the exception. It keeps its events in the Event Log and
            has no built-in way to forward them as syslog, so the domain
            controller and the Windows clients each run{" "}
            <span className="fw-bolder">NXLog</span> to bridge the gap. pfSense
            supports remote syslog natively, so it only needed a setting
            turned on.
          </p>

          <h3>Why Loki and Grafana</h3>
          <p className="m-5">
            I use <span className="fw-bolder">Grafana Loki</span> as the lab's
            central log platform. It covers the collection and search side of
            what a SIEM does, but on its own it isn't a full SIEM. It doesn't
            ship with a library of detection rules, it doesn't normalize logs
            into a common schema, and it only correlates across sources when
            I write the query that does it. Grafana sits on top for querying
            and dashboards, and it can alert on those queries too, so
            detection is something I can build on later. For now, it's the
            one place where all of the lab's logs live and can be searched.
          </p>
          <p className="m-5">
            I chose it over <span className="fw-bolder">Wazuh</span>, which
            is the obvious open-source alternative. Wazuh is a much more
            complete security platform, but it felt heavy and like a lot to
            set up and maintain for a lab this size. Loki on a small Ubuntu
            Server VM is very light, it's free, and it deploys with Docker
            Compose in one file. Loki keeps
            resource use low by indexing only labels, like the host name, and
            storing the log lines themselves compressed, instead of indexing
            every word. The trade-off is that searching inside message text
            is slower and less powerful than in a full-text engine, which is
            fine at lab scale.
          </p>

          <h3>How It's Put Together</h3>
          <p className="m-5">
            The collector is an Ubuntu Server VM added to the lab network
            behind pfSense. The path a log takes looks like this:
          </p>

          <div className="topology-diagram pipeline-diagram">
            <Zoomable label="Log pipeline diagram">
              <svg viewBox="0 0 560 240" role="img" aria-labelledby="pipeline-title">
                <title id="pipeline-title">
                  Log pipeline: sources send syslog to rsyslog, which writes
                  per-host files. Promtail reads those files and pushes to
                  Loki, and Grafana queries Loki.
                </title>

                <g stroke="crimson" strokeWidth="2.5" fill="none">
                  <line x1="170" y1="55" x2="205" y2="55" />
                  <line x1="355" y1="55" x2="390" y2="55" />
                  <line x1="465" y1="90" x2="465" y2="150" />
                  <line x1="390" y1="185" x2="355" y2="185" />
                  <line x1="205" y1="185" x2="170" y2="185" />
                </g>
                <g fill="crimson">
                  <polygon points="205,55 197,50 197,60" />
                  <polygon points="390,55 382,50 382,60" />
                  <polygon points="465,150 460,142 470,142" />
                  <polygon points="355,185 363,180 363,190" />
                  <polygon points="170,185 178,180 178,190" />
                </g>

                <g>
                  <rect x="20" y="20" width="150" height="70" rx="10" fill="#101820" stroke="#8fa3ad" strokeWidth="2" />
                  <text x="95" y="50" textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700">Sources</text>
                  <text x="95" y="72" textAnchor="middle" fill="#c9d3d8" fontSize="12">pfSense · Windows</text>
                </g>
                <g>
                  <rect x="205" y="20" width="150" height="70" rx="10" fill="#101820" stroke="crimson" strokeWidth="2" />
                  <text x="280" y="50" textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700">rsyslog</text>
                  <text x="280" y="72" textAnchor="middle" fill="#c9d3d8" fontSize="12">UDP 514</text>
                </g>
                <g>
                  <rect x="390" y="20" width="150" height="70" rx="10" fill="#101820" stroke="crimson" strokeWidth="2" />
                  <text x="465" y="50" textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700">Host files</text>
                  <text x="465" y="72" textAnchor="middle" fill="#c9d3d8" fontSize="12">/var/log/remote</text>
                </g>
                <g>
                  <rect x="390" y="150" width="150" height="70" rx="10" fill="#101820" stroke="crimson" strokeWidth="2" />
                  <text x="465" y="180" textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700">Promtail</text>
                  <text x="465" y="202" textAnchor="middle" fill="#c9d3d8" fontSize="12">Docker</text>
                </g>
                <g>
                  <rect x="205" y="150" width="150" height="70" rx="10" fill="#101820" stroke="crimson" strokeWidth="2" />
                  <text x="280" y="180" textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700">Loki</text>
                  <text x="280" y="202" textAnchor="middle" fill="#c9d3d8" fontSize="12">Docker</text>
                </g>
                <g>
                  <rect x="20" y="150" width="150" height="70" rx="10" fill="#101820" stroke="crimson" strokeWidth="2" />
                  <text x="95" y="180" textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700">Grafana</text>
                  <text x="95" y="202" textAnchor="middle" fill="#c9d3d8" fontSize="12">Docker</text>
                </g>
              </svg>
            </Zoomable>
          </div>

          <p className="m-5">
            <span className="fw-bolder">rsyslog</span> is the front door. It
            listens for syslog on UDP 514 and writes every incoming message to
            a file named after the host that sent it. Those raw files stay on
            disk as an untouched copy of what each machine reported, which
            also suits the forensics side of this lab. The listener is enabled
            in the main rsyslog config. The TCP version is there too, just
            commented out:
          </p>

          <pre>
            <code>{RSYSLOG_LISTENER}</code>
          </pre>

          <p className="m-5">
            My own rule lives in its own file under{" "}
            <span className="fw-bolder">/etc/rsyslog.d</span>. It has a
            template for the filename, a template for the line format, and
            the rule that uses both. The line format rewrites every timestamp
            to UTC as the message is written, which I explain in the time
            zone section below:
          </p>

          <pre>
            <code>{RSYSLOG}</code>
          </pre>

          <p className="m-5">
            The <span className="fw-bolder">*.*</span> rule has no filter, so
            it also catches the server's own local logs. That's why the
            collector shows up as a host alongside the machines it's
            collecting from.
          </p>

          <p className="m-5">
            Loki, Promtail, and Grafana run as three containers in one Docker
            Compose file:
          </p>

          <pre>
            <code>{COMPOSE}</code>
          </pre>

          <p className="m-5">
            Only Grafana and Loki publish ports. Promtail doesn't need one, it
            just reads the host log folder through a read-only mount, so it
            can't modify the raw files it's shipping. Its config tells it to
            tail every <span className="fw-bolder">.log</span> file in that
            folder and pull the host name out of the filename, which becomes
            the <span className="fw-bolder">host</span> label I filter on in
            Grafana:
          </p>

          <pre>
            <code>{PROMTAIL}</code>
          </pre>

          <p className="m-5">
            Loki itself is a single-node setup with local filesystem storage.
            The setting I pay the most attention to is retention. It's set to
            7 days, with the compactor enabled so old data is actually
            deleted instead of just being ignored. The number itself lives in
            a small <span className="fw-bolder">.env</span> file next to the
            compose file, as <span className="fw-bolder">RETENTION_DAYS=7</span>.
            Compose passes it into the container, and the Loki config reads it
            through the <span className="fw-bolder">expand-env</span> flag.
            Moving to 30 days means changing that one number and running{" "}
            <span className="fw-bolder">docker compose up -d</span> again.
          </p>

          <pre>
            <code>{LOKI}</code>
          </pre>

          <h3>Getting Each Source to Send</h3>
          <p className="m-5">
            <span className="fw-bolder">pfSense</span> was the easy one. Under
            Status, System Logs, Settings there's a remote logging option. I
            enabled it, pointed it at the collector on port 514, and turned on
            every log category.
          </p>
          <p className="m-5">
            <span className="fw-bolder">The Windows machines</span> use NXLog
            Community Edition. The config is short: it reads the Security,
            System, and Application event logs, converts each event to syslog,
            and sends it to the collector over UDP. The conversion uses the
            IETF syslog format (RFC 5424), which includes the time zone
            offset in every timestamp. I started with the Snare format, which
            keeps more of Windows' event fields separate, but its timestamps
            had no time zone, and that caused a problem I cover below. The
            trade-off is that the IETF output is mostly just the message
            text, and it dropped the Windows event ID, which Snare carried as
            its own field. For forensics that matters, since event IDs like
            4624 and 4625 are how you search Windows logs. So the config adds
            it back to the front of each message with one extra line, which
            means I can still search Loki for something like{" "}
            <span className="fw-bolder">EventID=4625</span>.
          </p>

          <pre>
            <code>{NXLOG}</code>
          </pre>

          <p className="m-5">
            Two choices in there are deliberate. Collecting everything in the
            Security log is noisy, one Windows client alone sent thousands
            of events within minutes of connecting, but in a lab I would
            rather have everything and filter later. And UDP is fire and
            forget, with no delivery guarantee. That's acceptable here, and
            it's the first thing I'd change, to TCP or TLS, on anything that
            mattered.
          </p>

          <h3>A Troubleshooting Note</h3>
          <p className="m-5">
            When I connected the domain controller, nothing showed up. The
            NXLog log file said{" "}
            <span className="fw-bolder">no functional input modules</span> and{" "}
            <span className="fw-bolder">no routes defined</span>, which means
            the service had started but was running without my config. I had
            edited the file after the service was already running and never
            restarted it. Validating the config with{" "}
            <span className="fw-bolder">nxlog.exe -v -c</span> and then
            running <span className="fw-bolder">Restart-Service nxlog</span>{" "}
            fixed it, and events started flowing right away.
          </p>

          <h3>Seeing It Work</h3>
          <p className="m-5">
            In Grafana's Explore view, splitting by the{" "}
            <span className="fw-bolder">host</span> label shows each source as
            its own card. Here the domain controller, a Windows client, and
            the collector itself are all reporting into the same place.
          </p>

          <div className="screenshot-frame">
            <Zoomable label="Grafana Explore showing logs from three hosts">
              <img
                src={grafanaHosts}
                width="1318"
                height="751"
                alt="Grafana Explore split by host label, showing log volume and recent entries for a Windows client, the domain controller, and the Loki server, each with UTC timestamps inside the messages and Windows entries carrying an EventID"
              />
            </Zoomable>
          </div>
          <span className="screenshot-caption">
            Three hosts reporting into Loki, split by the host label. The
            timestamp inside every message is UTC.
          </span>

          <h3>A Time Zone Problem</h3>
          <p className="m-5">
            Getting here exposed a problem along the way. Each line in
            Grafana has two timestamps: the one at the start is when Loki
            received it, and the one inside the message is what the sending
            machine wrote. At first those disagreed. The domain controller is set to Eastern time and one
            Windows client was set to Pacific, and both were labeled{" "}
            <span className="fw-bolder">+00:00</span>, as if they were UTC.
            One client's logs read about three hours behind everything else.
          </p>
          <p className="m-5">
            The cause was the Snare format. Its timestamp is the old BSD
            syslog style, which has no time zone in it, so rsyslog had to
            guess, and it assumed the sender was in the server's own zone,
            which is UTC. Switching NXLog to the IETF format fixed it, because
            that timestamp carries the real offset. For a while the domain
            controller and the client both showed{" "}
            <span className="fw-bolder">-04:00</span>, the collector showed
            UTC, and every one of them lined up with when the entry arrived.
          </p>
          <p className="m-5">
            That left the files with a mix of offsets, which is still awkward
            to read. So I made the collector normalize everything. The line
            format template above uses rsyslog's{" "}
            <span className="fw-bolder">date-utc</span> option, so every
            line written to disk now carries a UTC timestamp, whatever zone
            the sender is in. In the screenshot above, every message reads{" "}
            <span className="fw-bolder">+00:00</span>. The timestamp at the
            start of each line is still shown in my browser's local time,
            Eastern, which is why an entry that reads 05:14 UTC inside the
            message arrived at 01:14 in Grafana.
          </p>
          <p className="m-5">
            I considered setting every machine to UTC instead. I decided
            against it, because the collector shouldn't depend on every
            source being configured the same way. If a host ends up in
            another time zone, its logs should still line up. The trade-off
            is that the file no longer shows the sender's original offset,
            but the moment in time is the same. For a DFIR lab, that's the
            point: when you rebuild a timeline from many machines, the
            timestamps have to mean the same thing.
          </p>

          <h3>A Change to the Lab: Kali Is Now the Forensics Box</h3>
          <p className="m-5">
            Part 2 described Kali as the attacker machine and CAINE as the
            forensics tool. That's changed. CAINE boots from live media, so
            every session starts from scratch and I have to attach a disk each
            time just to have somewhere to keep my work. Kali is installed
            and persistent, so my tools, notes, and evidence stay where I left
            them. Kali is now my main forensics workstation.
          </p>
          <p className="m-5">
            It's also the one machine that does <span className="fw-bolder">not</span>{" "}
            send logs to the collector. Everything else in the lab, from the
            firewall to the domain controller and the clients, reports in.
            Part 2 stays as it was when I wrote it, and this is the update.
          </p>

          <h3>Known Limitations</h3>
          <p className="m-5">
            Promtail has since been deprecated by Grafana in favor of Alloy,
            so a future rebuild would use that instead. I used Promtail
            because it's what the guides I learned from used, and it still
            does the job. Its position tracking is also stored in a temporary
            path inside the container, so recreating the container can make it
            re-read files from the start. Neither matters much at lab scale,
            but both are worth knowing about.
          </p>

          <p className="m-5">
            <span className="fw-bolder">Up next:</span> with the logs in
            place, the lab has what it needs for the forensics and detection
            work this series was built for, and the next part picks up from
            there.
          </p>

          <h3>References</h3>
          <p className="m-5">
            These are the write-ups I used to get started:
          </p>
          <ul>
            <li>
              <a
                href="https://www.virtualizationhowto.com/2022/11/grafana-loki-configuration-syslog-server-for-home-labs/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Grafana Loki Configuration Syslog Server for Home Labs
                (Virtualization Howto)
              </a>
            </li>
            <li>
              <a
                href="https://homelab.anita-fred.net/grafana/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Grafana Logging and Monitoring (Our Home Lab)
              </a>
            </li>
            <li>
              <a
                href="https://scott-wheeler.com/blog/centralized-logging-with-grafana-loki/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Centralized Logging for a Homelab with Grafana, Loki, and
                Alloy (Scott Wheeler)
              </a>
            </li>
            <li>
              <a
                href="https://community.grafana.com/t/rsyslog-promtail-loki-grafana-help/109266"
                target="_blank"
                rel="noopener noreferrer"
              >
                Rsyslog/promtail/loki/grafana help (Grafana Labs Community
                Forums)
              </a>
            </li>
          </ul>

          <SeriesNav current="/digital-forensics-homelab-logging/" />
        </div>
        <Footer2 />
      </section>
    </div>
  );
}

export default DigitalForensicsHomelabLogging;
